import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import {
  fetchBlogs,
  addBlog,
  updateBlog,
  deleteBlog,
  addCategory,
  addLabel,
  fetchCategories,
  fetchLabels,
  updateCategory,
  deleteCategory,
  updateLabel,
  deleteLabel,
} from "../api/blogApi";
import toast from "react-hot-toast";
import { useEffect } from "react";
export const useBlogs = () => {
  const queryClient = useQueryClient();

  // Fetch all blogs
  const {
    data: blogs,
    error,
    isLoading,
  } = useQuery({
    queryKey: ["blogs"],
    queryFn: fetchBlogs,
  });

  // Add a new blog
  const addNewBlogMutation = useMutation({
    mutationFn: addBlog,
    onSuccess: (data) => {
      queryClient.invalidateQueries(["blogs"]);
      toast.success(data.message || "Blog added successfully!");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to add blog ");
    },
  });

  // Update an existing blog
  const updateBlogMutation = useMutation({
    mutationFn: ({ id, blogData }) => updateBlog(id, blogData),
    onSuccess: (data) => {
      queryClient.invalidateQueries(["blogs"]);
      toast.success(data.message || "Blog updated successfully!");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to update blog");
    },
  });

  // Delete a blog
  const deleteBlogMutation = useMutation({
    mutationFn: (id) => deleteBlog(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries(["blogs"]);
      toast.success(data.message || "Blog deleted successfully!");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to delete blog");
    },
  });

  // Fetch categories
  const { data: categories, isLoading: isCategoriesLoading, error:categoryError } = useQuery({
    queryKey: ["categories"],
    queryFn: fetchCategories,
  });

  // Fetch labels
  const { data: labels, isLoading: isLabelsLoading, error:labelError } = useQuery({
    queryKey: ["labels"],
    queryFn: fetchLabels,
  });

  // Add a new category
  const addNewCategoryMutation = useMutation({
    mutationFn: addCategory,
    onSuccess: (data) => {
      queryClient.invalidateQueries(["categories"]);
      toast.success(data.message || "Category added successfully!");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to add category ");
    },
  });

  // Update an existing category
  const updateCategoryMutation = useMutation({
    mutationFn: ({ id, categoryData }) => updateCategory(id, categoryData),
    onSuccess: (data) => {
      queryClient.invalidateQueries(["categories"]);
      toast.success(data.message || "Category updated successfully!");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to update category");
    },
  });

    // Delete a category
    const deleteCategoryMutation = useMutation({
    mutationFn: (id) => deleteCategory(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries(["categories"]);
      toast.success(data.message || "Category deleted successfully!");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to delete category");
    }
    })

  // Add a new label
  const addNewLabelMutation = useMutation({
    mutationFn: addLabel,
    onSuccess: (data) => {
      queryClient.invalidateQueries(["labels"]);
      toast.success(data.message || "Label added successfully!");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to add label ");
    },
  });

    // Update an existing label
    const updateLabelMutation = useMutation({
    mutationFn: ({ id, labelData }) => updateLabel(id, labelData),
    onSuccess: (data) => {
      queryClient.invalidateQueries(["labels"]);
      toast.success(data.message || "Label updated successfully!");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to update label");
    }
    })

    // Delete a label
const deleteLabelMutation = useMutation({
    mutationFn: (id) => deleteLabel(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries(["labels"]);
      toast.success(data.message || "Label deleted successfully!");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to delete label");
    }
})

  useEffect(() => {
    // This effect runs when the component mounts or when blogs change
    if (error || categoryError || labelError) {
        // Handle errors globally
        const errorMessage = error?.response?.data?.message || "An error occurred";
        const categoryErrorMessage = categoryError?.response?.data?.message || "Failed to fetch categories";
        const labelErrorMessage = labelError?.response?.data?.message || "Failed to fetch labels";

        toast.error(errorMessage || categoryErrorMessage || labelErrorMessage);
    }
  }, [error, categoryError, labelError]);

  return {
    blogs,
    isLoading,
    addNewBlogMutation,
    updateBlogMutation,
    deleteBlogMutation,
    categories,
    labels,
    isCategoriesLoading,
    isLabelsLoading,
    addNewCategoryMutation,
    updateCategoryMutation,
    deleteCategoryMutation,
    addNewLabelMutation,
    updateLabelMutation,
    deleteLabelMutation,
  };
};
